import { Box, MenuItem, Popover, Stack, TextField } from "@mui/material";
import { Button, Typography } from "@platform/design-system";
import {
  HeapedAmountFields,
  parseClampedPercent,
} from "@platform/plan-grid-core";
import { CustomCellEditorProps } from "ag-grid-react";
import { forwardRef, useImperativeHandle, useRef, useState } from "react";

import {
  COMMISSION_TYPE_LABELS,
  COMMISSION_TYPE_VALUES,
} from "../compsConstants";

import { isCommissionAmountDisabled } from "./formatCommissionDisplay";

import type {
  CompsTableCommissionType,
  CompsTableHeapedAmount,
} from "@platform/api";
import type { CarrierCommissionCell, RawCommissionAmount } from "../types";

const isHeapedAmount = (
  value: RawCommissionAmount,
): value is CompsTableHeapedAmount =>
  value !== null &&
  typeof value === "object" &&
  "first" in value &&
  "thereafter" in value;

/** Amount clamp/precision by type, matching the prior per-row `agNumberCellEditor` params. */
const amountBoundsForType = (
  type: CompsTableCommissionType | null,
): { min: number; max?: number } =>
  type === "pepm" ? { min: 0 } : { min: 0, max: 100 };

const resolveInitialCell = (value: unknown): CarrierCommissionCell | null => {
  if (!value || typeof value !== "object") return null;
  return value as CarrierCommissionCell;
};

const resolveInitialAmountString = (
  cell: CarrierCommissionCell | null,
): string => (typeof cell?.amount === "number" ? String(cell.amount) : "");

const resolveInitialHeaped = (
  cell: CarrierCommissionCell | null,
): CompsTableHeapedAmount | null => {
  const amount = cell?.amount ?? null;
  return isHeapedAmount(amount) ? amount : null;
};

export interface EditCommissionEditorRef {
  getValue: () => CarrierCommissionCell | null;
  isPopup?: () => boolean;
}

interface CommissionAmountFieldProps {
  commissionType: CompsTableCommissionType | null;
  amountHidden: boolean;
  draftAmount: string;
  onDraftAmountChange: (value: string) => void;
  heapedFirst: string;
  heapedThereafter: string;
  onHeapedFirstChange: (value: string) => void;
  onHeapedThereafterChange: (value: string) => void;
}

/**
 * The Amount side of the modal — hidden entirely for types that don't take an
 * amount, replaced inline by the heaped First Year/Thereafter fields when the
 * type is Heaped, otherwise a plain number field.
 */
const CommissionAmountField = ({
  commissionType,
  amountHidden,
  draftAmount,
  onDraftAmountChange,
  heapedFirst,
  heapedThereafter,
  onHeapedFirstChange,
  onHeapedThereafterChange,
}: CommissionAmountFieldProps): React.JSX.Element | null => {
  if (commissionType === "heaped") {
    return (
      <HeapedAmountFields
        first={heapedFirst}
        thereafter={heapedThereafter}
        onFirstChange={onHeapedFirstChange}
        onThereafterChange={onHeapedThereafterChange}
      />
    );
  }
  if (amountHidden) return null;

  const bounds = amountBoundsForType(commissionType);
  return (
    <TextField
      label="Amount"
      type="number"
      value={draftAmount}
      onChange={(e): void => onDraftAmountChange(e.target.value)}
      inputProps={{ min: bounds.min, max: bounds.max, step: 0.01 }}
      fullWidth
    />
  );
};

/**
 * "Edit Commission" popup cell editor for the Comps grid — combines the
 * commission Type and Amount into one modal, matching the Quotes tab's Edit
 * Benefit UX. Commits both fields together in a single value, so Save fires
 * exactly one `onCellValueChanged` (no separate type/amount race).
 */
export const EditCommissionEditor = forwardRef<
  EditCommissionEditorRef,
  CustomCellEditorProps<unknown, CarrierCommissionCell>
>((props, ref) => {
  const { stopEditing, api, node, column } = props;
  const onValueChange = (
    props as unknown as {
      onValueChange?: (value: CarrierCommissionCell | null) => void;
    }
  ).onValueChange;

  const initialCell = resolveInitialCell(props.value);
  const initialHeaped = resolveInitialHeaped(initialCell);

  const [draftType, setDraftType] = useState<CompsTableCommissionType | null>(
    initialCell?.commissionType ?? null,
  );
  const [draftAmount, setDraftAmount] = useState<string>(
    resolveInitialAmountString(initialCell),
  );
  const [heapedFirst, setHeapedFirst] = useState<string>(
    initialHeaped?.first?.toString() ?? "",
  );
  const [heapedThereafter, setHeapedThereafter] = useState<string>(
    initialHeaped?.thereafter?.toString() ?? "",
  );

  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(true);

  const amountHidden = isCommissionAmountDisabled(draftType);
  const isHeaped = draftType === "heaped";

  const resolveAmount = (): RawCommissionAmount => {
    if (amountHidden) return null;
    if (isHeaped) {
      const first = parseClampedPercent(heapedFirst);
      const thereafter = parseClampedPercent(heapedThereafter);
      if (first === null && thereafter === null) return null;
      return { first: first ?? 0, thereafter: thereafter ?? 0 };
    }
    if (draftAmount === "") return null;
    const parsed = Number(draftAmount);
    if (!Number.isFinite(parsed)) return null;

    const { min, max } = amountBoundsForType(draftType);
    const clamped = Math.max(min, max == null ? parsed : Math.min(max, parsed));
    return Math.round(clamped * 100) / 100;
  };

  const commit = (value: CarrierCommissionCell | null): void => {
    if (onValueChange) {
      onValueChange(value);
    } else if (node && column) {
      node.setDataValue(column.getColId(), value);
    }
    setOpen(false);
    if (api) {
      api.stopEditing();
    } else {
      stopEditing();
    }
  };

  useImperativeHandle(ref, () => ({
    getValue: () =>
      initialCell
        ? {
            ...initialCell,
            commissionType: draftType,
            amount: resolveAmount(),
          }
        : null,
    isPopup: () => true,
  }));

  const handleSave = (): void => {
    if (!initialCell) {
      commit(null);
      return;
    }
    commit({
      ...initialCell,
      commissionType: draftType,
      amount: resolveAmount(),
    });
  };

  const handleCancel = (): void => {
    setOpen(false);
    stopEditing(true);
    api?.stopEditing(true);
  };

  const handleTypeChange = (value: string): void => {
    const nextType = (value || null) as CompsTableCommissionType | null;
    setDraftType(nextType);
    if (nextType && isCommissionAmountDisabled(nextType)) {
      setDraftAmount("");
    }
  };

  return (
    <Box ref={anchorRef}>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleCancel}
        onMouseDown={(e): void => e.stopPropagation()}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disableAutoFocus
        disableEnforceFocus
        slotProps={{ paper: { className: "ag-custom-component-popup" } }}
      >
        <Box sx={{ p: 2, minWidth: 280 }}>
          <Typography variant="header-small" gutterBottom>
            Edit Commission Value
          </Typography>

          <Stack spacing={2}>
            <TextField
              select
              label="Type"
              value={draftType ?? ""}
              onChange={(e): void => handleTypeChange(e.target.value)}
              fullWidth
            >
              {COMMISSION_TYPE_VALUES.map((value) => (
                <MenuItem key={value} value={value}>
                  {COMMISSION_TYPE_LABELS[value]}
                </MenuItem>
              ))}
            </TextField>

            <CommissionAmountField
              commissionType={draftType}
              amountHidden={amountHidden}
              draftAmount={draftAmount}
              onDraftAmountChange={setDraftAmount}
              heapedFirst={heapedFirst}
              heapedThereafter={heapedThereafter}
              onHeapedFirstChange={setHeapedFirst}
              onHeapedThereafterChange={setHeapedThereafter}
            />
          </Stack>

          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button size="sm" variant="secondary" onClick={handleCancel}>
              Cancel
            </Button>
            <Button size="sm" variant="primary" onClick={handleSave}>
              Save
            </Button>
          </Stack>
        </Box>
      </Popover>
    </Box>
  );
});

EditCommissionEditor.displayName = "EditCommissionEditor";
